/* 🥚⚙️ Auto-Synthesis Eggs — parallel-incubation crafting pipeline (Feb 2026).
   Drops from kills with rarity-weighted RNG (Common 55 / Uncommon 30 /
   Rare 12 / Legendary 3). Cosmic only via owner-grant or future secret unlock.

   Director's spec:
     • 100% automated incubation — egg slides in, timer starts.
     • All eggs tick in parallel (no slot cap).
     • Crack-ready → harvest crafting part.
     • Cannot upgrade. Cannot skip except via Omni Dollars. */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatCountdown(seconds) {
  if (seconds <= 0) return "READY";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function SynthEggsPage() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/synthesis/eggs/state`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load eggs.");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  // Tick once a second for countdowns. Refresh every 30s to catch newly-dropped eggs from combat.
  useEffect(() => {
    const a = setInterval(() => setTick(t => t + 1), 1000);
    const b = setInterval(refresh, 30000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [refresh]);

  // 📜 Whispers from the Realm — anonymized community whispers (public, no auth)
  const [whispers, setWhispers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/realm/rumors?window_hours=168`);
        if (!cancelled) setWhispers(r.data?.whispers || []);
      } catch (e) { /* silent */ }
    })();
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/realm/rumors?window_hours=168`);
        if (!cancelled) setWhispers(r.data?.whispers || []);
      } catch (e) { /* silent */ }
    }, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const onCrack = async (egg_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/synthesis/eggs/crack`, { egg_id });
      toast.success(`💥 +1 ${r.data.part_icon} ${r.data.part_label}!`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Crack failed.");
    } finally { setBusy(false); }
  };

  const onInstantHatch = async (egg, cost) => {
    if (busy) return;
    if (!window.confirm(`Spend ${cost} 🪙 Omni Dollars to instantly hatch this ${egg.tier?.label} egg?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/synthesis/eggs/instant-hatch`, { egg_id: egg.egg_id });
      toast.success(`⚡ Egg ready to crack!`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Instant hatch failed.");
    } finally { setBusy(false); }
  };

  if (!state) return <div className="p-8 text-center text-neutral-500">Loading synthesis eggs…</div>;

  // Sort: ready first, then by smallest time-remaining
  const sortedEggs = [...(state.eggs || [])].sort((a, b) => {
    if (a.ready !== b.ready) return a.ready ? -1 : 1;
    return (a.seconds_to_hatch || 0) - (b.seconds_to_hatch || 0);
  });

  // Live-recompute the remaining seconds on every tick
  const liveEggs = sortedEggs.map(e => {
    if (!e.hatches_at) return e;
    const remain = Math.max(0, Math.floor((new Date(e.hatches_at).getTime() - Date.now()) / 1000));
    return { ...e, seconds_to_hatch: remain, ready: remain <= 0 };
  });
  void tick;  // referenced so the eslint-no-unused doesn't strip the tick state

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] via-[#101018] to-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <button
              data-testid="synth-eggs-back"
              onClick={() => navigate(-1)}
              className="text-xs text-neutral-500 hover:text-neutral-300 mb-2">
              ← Back
            </button>
            <h1 className="text-3xl sm:text-4xl font-black tracking-wide" style={{ fontFamily: 'Anton, sans-serif' }}>
              🥚⚙️ <span className="text-amber-300">AUTO-SYNTHESIS EGGS</span>
            </h1>
            <p className="text-xs text-neutral-400 mt-1 max-w-xl">
              Eggs drop from combat. They auto-incubate the moment they land in your inventory.
              Crack ready eggs to harvest crafting parts for the Blacksmith.
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="px-3 py-2 rounded-lg border border-amber-600/40 bg-amber-900/20"
                 data-testid="synth-eggs-od">
              <div className="text-[10px] uppercase tracking-wider text-amber-400">Omni Dollars</div>
              <div className="text-2xl font-black text-amber-300">🪙 {state.omni_dollars}</div>
            </div>
            <div className="px-3 py-2 rounded-lg border border-emerald-600/40 bg-emerald-900/20 text-center"
                 data-testid="synth-eggs-ready-count">
              <div className="text-[10px] uppercase tracking-wider text-emerald-400">Ready to Crack</div>
              <div className="text-2xl font-black text-emerald-300">{liveEggs.filter(e => e.ready).length}</div>
            </div>
          </div>
        </div>

        {/* 📜 Whispers from the Realm — anonymized, public, suspense-building */}
        {whispers.length > 0 && (
          <div className="rounded-xl border border-purple-700/40 bg-gradient-to-br from-purple-950/30 via-black to-black p-4 mb-6"
               data-testid="realm-whispers">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-xs uppercase tracking-[0.3em] text-purple-300/70">📜 Whispers from the Realm</div>
              <div className="text-[10px] text-neutral-500 italic">past 7 days · anonymous</div>
            </div>
            <div className="space-y-1.5">
              {whispers.map((w) => (
                <div key={w.type}
                     data-testid={`whisper-${w.type}`}
                     className="flex items-center gap-2 text-xs text-purple-200/90 italic">
                  <span className="text-base">{w.icon}</span>
                  <span>{w.whisper}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Parts inventory */}
        <div className="rounded-xl border border-neutral-800 bg-black/30 p-4 mb-6">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">🛠️ Forging Parts</div>          <div className="flex flex-wrap gap-3">
            {Object.entries(state.tier_catalog || {}).map(([rid, t]) => {
              if (t.redacted) {
                const owned = (state.parts || {})[t.part_id] || 0;
                if (owned <= 0) return null;  // hide cosmic if user has none
                return (
                  <div key={rid}
                       className="px-3 py-2 rounded-lg border-2 border-purple-500/60 bg-gradient-to-br from-purple-900/40 to-black/40 text-sm"
                       data-testid={`part-${t.part_id}`}>
                    <div className="text-xl">{t.part_icon}</div>
                    <div className="font-bold text-purple-200">{owned}</div>
                    <div className="text-[9px] text-purple-300">{t.part_label}</div>
                  </div>
                );
              }
              const owned = (state.parts || {})[t.part_id] || 0;
              return (
                <div key={rid}
                     className="px-3 py-2 rounded-lg border bg-black/40 text-sm"
                     style={{ borderColor: t.color + "60" }}
                     data-testid={`part-${t.part_id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{t.part_icon}</span>
                    <div>
                      <div className="font-bold" style={{ color: t.color }}>{owned}</div>
                      <div className="text-[9px] text-neutral-500">{t.part_label}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {Object.values(state.parts || {}).every(v => !v) && (
              <div className="text-xs text-neutral-500 italic">
                No parts yet. Defeat mobs to drop synthesis eggs.
              </div>
            )}
          </div>
        </div>

        {/* Egg incubators */}
        {liveEggs.length === 0 ? (
          <div className="rounded-xl border border-neutral-800 bg-black/30 p-10 text-center" data-testid="synth-eggs-empty">
            <div className="text-5xl mb-3 opacity-50">🥚</div>
            <div className="text-neutral-400">No eggs incubating right now.</div>
            <div className="text-xs text-neutral-500 mt-2">Eggs drop from combat at 20% (40% on boss kills).</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="synth-eggs-list">
            {liveEggs.map(egg => {
              const tier = egg.tier || {};
              const isCosmic = !!egg.tier_redacted;
              const cost = tier.instant_hatch_od_cost || 0;
              return (
                <div key={egg.egg_id}
                     data-testid={`egg-${egg.egg_id}`}
                     className={`rounded-xl border-2 p-4 ${
                       egg.ready
                         ? "border-emerald-400 shadow-[0_0_20px_rgba(52,211,153,0.4)] bg-gradient-to-br from-emerald-900/30 to-black/40"
                         : isCosmic
                           ? "border-purple-500/60 bg-gradient-to-br from-purple-900/30 to-black/40"
                           : "border-neutral-700 bg-gradient-to-br from-neutral-900/50 to-black/40"
                     }`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-3xl mb-1">{tier.icon}</div>
                      <div className="text-[10px] uppercase tracking-wider"
                           style={{ color: tier.color || "#888" }}>
                        {tier.label}
                      </div>
                    </div>
                    {egg.ready && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-black text-[10px] font-black animate-pulse">
                        READY
                      </span>
                    )}
                  </div>
                  <div className="text-sm">
                    {isCosmic ? (
                      <div className="text-purple-300 italic text-[11px]">
                        Hums with an unfamiliar resonance. The Blacksmith's runic anvil might know what to do with it…
                      </div>
                    ) : (
                      <>
                        <div className="font-bold text-amber-200">{tier.part_icon} {tier.part_label}</div>
                        <div className="text-[10px] text-neutral-500 mt-1">{tier.part_blurb}</div>
                      </>
                    )}
                  </div>
                  <div className="mt-3">
                    {egg.ready ? (
                      <button
                        data-testid={`egg-crack-${egg.egg_id}`}
                        onClick={() => onCrack(egg.egg_id)}
                        disabled={busy}
                        className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-black tracking-wider">
                        💥 CRACK OPEN
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs">
                          <div className="text-neutral-400">⏳</div>
                          <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
                            <div
                              className="h-full transition-all"
                              style={{
                                width: `${100 - (egg.seconds_to_hatch / (tier.incubation_hours * 3600)) * 100}%`,
                                background: `linear-gradient(90deg, ${tier.color || "#888"}, ${tier.color || "#fff"})`,
                              }}
                            />
                          </div>
                          <span className="text-neutral-400 font-mono text-[10px]"
                                data-testid={`egg-countdown-${egg.egg_id}`}>
                            {formatCountdown(egg.seconds_to_hatch)}
                          </span>
                        </div>
                        {cost > 0 && (
                          <button
                            data-testid={`egg-instant-${egg.egg_id}`}
                            onClick={() => onInstantHatch(egg, cost)}
                            disabled={busy || state.omni_dollars < cost}
                            className="w-full py-2 rounded-lg bg-amber-700 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold">
                            🪙 Instant Hatch ({cost} OD)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tier reference card */}
        <div className="mt-6 rounded-xl border border-neutral-800 bg-black/30 p-4">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">📜 Tier Reference</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
            {Object.entries(state.tier_catalog || {}).map(([rid, t]) => (
              <div key={rid} className="px-2 py-1.5 rounded border bg-black/40"
                   style={{ borderColor: t.color + "40" }}>
                <div className="flex items-center gap-1">
                  <span>{t.icon}</span>
                  <span className="font-bold" style={{ color: t.color }}>{t.label}</span>
                </div>
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  ⏳ {t.incubation_hours}h · 🪙 {t.instant_hatch_od_cost} skip
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
